import type { FC, FormEvent, KeyboardEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/button'
import { isValidMobile } from '@/lib/validation'

import {
    ArrowLeft,
    ArrowRight,
    Eye,
    EyeOff,
    GraduationCap,
    Info,
    KeyRound,
    Lock,
    Phone,
    Presentation
} from 'lucide-react'

import {
    Field,
    MobileInput,
    SelectInput,
    TextInput,
    fieldDescription
} from '@/components/landing/form-fields'

import { centerOptions, contactPhones } from '@/constants/content'

type Role = 'student' | 'trainer'

type SignInField = 'center' | 'mobile' | 'password'

type SignInErrors = Partial<Record<SignInField, string>>

type View = 'form' | 'help'

const ROLES: Array<{ id: Role, label: string, icon: LucideIcon }> = [
    { id: 'student', label: "Student", icon: GraduationCap },
    { id: 'trainer', label: "Trainer", icon: Presentation }
]

const ROLE_COPY: Record<Role, { eyebrow: string, subtitle: string }> = {
    student: { eyebrow: "Student sign-in", subtitle: "Sign in to your academic portal." },
    trainer: { eyebrow: "Trainer sign-in", subtitle: "Sign in to manage your batches and sessions." }
}

const FIELD_ORDER: Array<SignInField> = ['center', 'mobile', 'password']

const CENTER_HINT = "The center you registered with."

const MOBILE_HINT = "The number you used when you registered."

const SignInCard: FC = (): ReactNode => {
    const [role, setRole] = useState<Role>('student')
    const [center, setCenter] = useState('')
    const [mobile, setMobile] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [errors, setErrors] = useState<SignInErrors>({})
    const [submitted, setSubmitted] = useState(false)
    const [view, setView] = useState<View>('form')
    const viewChanged = useRef(false)

    useEffect(() => {
        if (!viewChanged.current) return

        document.getElementById(view === 'help' ? 'signin-help-title' : 'signin-forgot')?.focus()
    }, [view])

    const changeView = (next: View): void => {
        viewChanged.current = true
        setSubmitted(false)
        setView(next)
    }

    const clearError = (field: SignInField): void => {
        setSubmitted(false)
        setErrors((current) => ({ ...current, [field]: undefined }))
    }

    const selectRole = (next: Role): void => {
        setRole(next)
        setSubmitted(false)
    }

    const handleRoleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return

        event.preventDefault()

        const next: Role = role === 'student' ? 'trainer' : 'student'

        selectRole(next)
        document.getElementById(`signin-role-${next}`)?.focus()
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const nextErrors: SignInErrors = {}

        if (!center) nextErrors.center = "Choose the training center you registered with."
        if (!isValidMobile(mobile)) nextErrors.mobile = mobile ? "Enter a valid 10-digit mobile number." : "Enter your registered mobile number."
        if (!password) nextErrors.password = "Enter your password."

        setErrors(nextErrors)

        const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field])

        if (firstInvalid) {
            setSubmitted(false)
            document.getElementById(`signin-${firstInvalid}`)?.focus()

            return
        }

        setSubmitted(true)
    }

    const copy = ROLE_COPY[role]

    return (
        <div id='sign-in' className='relative w-full max-w-md scroll-mt-24 justify-self-center lg:justify-self-end'>
            <div aria-hidden='true' className='absolute -inset-6 rounded-[2.5rem] bg-linear-to-br from-primary/45 via-primary/10 to-teal/35 opacity-80 blur-3xl' />

            <div className='relative overflow-hidden rounded-2xl bg-card text-card-foreground shadow-2xl shadow-black/40 ring-1 ring-white/10 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 motion-safe:duration-700'>
                <div aria-hidden='true' className='h-1 bg-linear-to-r from-primary via-violet-400 to-teal' />

                <div className='p-6 sm:p-8'>
                    {view === 'form' ? (
                        <div className='motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300'>
                            <div
                                role='radiogroup'
                                aria-label="Sign in as"
                                onKeyDown={handleRoleKeyDown}
                                className='grid grid-cols-2 gap-1 rounded-xl bg-muted p-1'
                            >
                                {ROLES.map((item) => {
                                    const active = item.id === role

                                    return (
                                        <button
                                            key={item.id}
                                            id={`signin-role-${item.id}`}
                                            type='button'
                                            role='radio'
                                            aria-checked={active}
                                            tabIndex={active ? 0 : -1}
                                            onClick={() => selectRole(item.id)}
                                            className={cn(
                                                'flex h-10 items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                                                active ? 'bg-white text-foreground shadow-sm ring-1 ring-black/5' : 'text-muted-foreground hover:text-foreground'
                                            )}
                                        >
                                            <item.icon className={cn('size-4', active && 'text-primary')} aria-hidden='true' />
                                            {item.label}
                                        </button>
                                    )
                                })}
                            </div>

                            <p className='mt-6 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-primary'>
                                {copy.eyebrow}
                            </p>

                            <h2 className='mt-2 text-2xl font-semibold text-foreground'>
                                Welcome back
                            </h2>

                            <p className='mt-1 text-sm text-muted-foreground'>
                                {copy.subtitle}
                            </p>

                            <form noValidate onSubmit={handleSubmit} className='mt-6 space-y-4'>
                                <Field id='signin-center' label="Training center" required hint={CENTER_HINT} error={errors.center}>
                                    <SelectInput
                                        id='signin-center'
                                        name='center'
                                        value={center}
                                        onChange={(event) => {
                                            setCenter(event.target.value)
                                            clearError('center')
                                        }}
                                        options={centerOptions}
                                        placeholder="Select your center"
                                        invalid={Boolean(errors.center)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('signin-center', errors.center, CENTER_HINT)}
                                    />
                                </Field>

                                <Field id='signin-mobile' label="Mobile number" required hint={MOBILE_HINT} error={errors.mobile}>
                                    <MobileInput
                                        id='signin-mobile'
                                        name='mobile'
                                        value={mobile}
                                        onValueChange={(value) => {
                                            setMobile(value)
                                            clearError('mobile')
                                        }}
                                        placeholder="98765 43210"
                                        invalid={Boolean(errors.mobile)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('signin-mobile', errors.mobile, MOBILE_HINT)}
                                    />
                                </Field>

                                <Field id='signin-password' label="Password" required error={errors.password}>
                                    <TextInput
                                        id='signin-password'
                                        name='password'
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete='current-password'
                                        value={password}
                                        onChange={(event) => {
                                            setPassword(event.target.value)
                                            clearError('password')
                                        }}
                                        placeholder="Enter your password"
                                        invalid={Boolean(errors.password)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('signin-password', errors.password)}
                                        icon={<Lock className='size-4' aria-hidden='true' />}
                                        trailing={(
                                            <button
                                                type='button'
                                                onClick={() => setShowPassword((current) => !current)}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                aria-pressed={showPassword}
                                                className='flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                                            >
                                                {showPassword
                                                    ? <EyeOff className='size-4' aria-hidden='true' />
                                                    : <Eye className='size-4' aria-hidden='true' />}
                                            </button>
                                        )}
                                    />
                                </Field>

                                <div className='flex justify-end'>
                                    <button
                                        id='signin-forgot'
                                        type='button'
                                        onClick={() => changeView('help')}
                                        className='rounded text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                                    >
                                        Forgot password?
                                    </button>
                                </div>

                                <button type='submit' className={buttonClass('primary', 'lg', 'group w-full')}>
                                    Sign in
                                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                                </button>

                                <div aria-live='polite'>
                                    {submitted && (
                                        <div className='flex gap-3 rounded-lg border border-primary/20 bg-secondary px-3.5 py-3 text-sm leading-relaxed text-secondary-foreground motion-safe:animate-in motion-safe:fade-in'>
                                            <Info className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />

                                            <p>
                                                This redesign isn't linked to live portal accounts yet, so nothing was sent. Sign-in starts working once it's connected to the Webify portal.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </form>

                            <p className='mt-6 border-t border-border pt-5 text-center text-sm text-muted-foreground'>
                                {role === 'student' ? (
                                    <>
                                        New to Webify?{' '}
                                        <a href='#register' className='font-semibold text-primary underline-offset-4 hover:underline'>
                                            Register for a program
                                        </a>
                                    </>
                                ) : (
                                    <>
                                        Trainer accounts are set up by your training center.
                                    </>
                                )}
                            </p>
                        </div>
                    ) : (
                        <div className='motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300'>
                            <button
                                type='button'
                                onClick={() => changeView('form')}
                                className='inline-flex items-center gap-1.5 rounded text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                            >
                                <ArrowLeft className='size-4' aria-hidden='true' />
                                Back to sign in
                            </button>

                            <span className='mt-6 flex size-12 items-center justify-center rounded-xl bg-secondary text-primary'>
                                <KeyRound className='size-6' aria-hidden='true' />
                            </span>

                            <h2 id='signin-help-title' tabIndex={-1} className='mt-5 text-2xl font-semibold text-foreground focus:outline-none'>
                                Reset your password
                            </h2>

                            <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                Your training center can help you get back into the portal. Call the team and share the mobile number you registered with.
                            </p>

                            <ul className='mt-6 space-y-2'>
                                {contactPhones.map((phone) => (
                                    <li key={phone.href}>
                                        <a
                                            href={phone.href}
                                            className='flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                                        >
                                            <span className='flex items-center gap-3'>
                                                <Phone className='size-4 text-primary' aria-hidden='true' />
                                                {phone.display}
                                            </span>

                                            <span className='text-xs font-medium text-muted-foreground'>
                                                Call
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default SignInCard
